import * as React from 'react';
import './ImageSelect.css';

interface IImageSelectOption {
  className?: string;
  selectedValue: string;
  value: string;
  onClick: (value: string) => void;
}

const ImageSelectOption: React.FC<IImageSelectOption> = function (props) {
  let className = 'ImageSelectOption-container';
  if (props.selectedValue === props.value) {
    className += ' ImageSelectOption-selected';
  }

  return (
    <button className={`ImageSelectOption-button ${props.className}`} onClick={() => props.onClick(props.value)}>
      <div className={className}>{props.children}</div>
    </button>
  );
};

export const LineImage: React.FC<{ id: number }> = function (props) {
  switch (props.id) {
    case 0:
      return <img alt="Line Normal" src="/line_normal.png" />;
    case 1:
      return <img alt="Line Dotted" src="/line_dotted.png" />;
    case 2:
      return <img alt="Line Dashed" src="/line_dashed.png" />;
    case 3:
      return <img alt="Line Circle" src="/line_circle.png" />;
  }

  throw new Error('unknown line type: ' + props.id);
};

interface ILineSelectProps {
  value: number;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number) => void;
}

export const LineSelect: React.FC<ILineSelectProps> = function (props) {
  return (
    <div className={props.containerClassName}>
      {[0, 1, 2, 3].map(i =>
        <ImageSelectOption
          key={i}
          className={props.optionClassName}
          selectedValue={props.value.toString()}
          value={i.toString()}
          onClick={(v) => props.onChange(parseInt(v))}
        >
          <LineImage id={i} />
        </ImageSelectOption>
      )}
    </div>
  );
};

interface IArrowEndSelectProps {
  value: number;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number) => void;
}

export const ArrowEndSelect: React.FC<IArrowEndSelectProps> = function (props) {
  return (
    <div className={props.containerClassName}>
      <ImageSelectOption
        className={props.optionClassName}
        selectedValue={props.value.toString()}
        value="0"
        onClick={(v) => props.onChange(parseInt(v))}
      >
        <img alt="Arrow-End None" src="/arrow_end_none.png" />
      </ImageSelectOption>
      <ImageSelectOption
        className={props.optionClassName}
        selectedValue={props.value.toString()}
        value="1"
        onClick={(v) => props.onChange(parseInt(v))}
      >
        <img alt="Arrow-End Arrow" src="/arrow_end_arrow.png" />
      </ImageSelectOption>
      <ImageSelectOption
        className={props.optionClassName}
        selectedValue={props.value.toString()}
        value="2"
        onClick={(v) => props.onChange(parseInt(v))}
      >
        <img alt="Arrow-End Circle" src="/arrow_end_circle.png" />
      </ImageSelectOption>
      <ImageSelectOption
        className={props.optionClassName}
        selectedValue={props.value.toString()}
        value="3"
        onClick={(v) => props.onChange(parseInt(v))}
      >
        <img alt="Arrow-End Cross" src="/arrow_end_cross.png" />
      </ImageSelectOption>
    </div>
  );
};

interface IEnclosingSvg {
  id: number;
  svg: string;
}

interface IEnclosingSvgSelectProps {
  enclosingSvgs: IEnclosingSvg[];
  value: number | null | undefined;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number | null) => void;
}

export const EnclosingSvgSelect: React.FC<IEnclosingSvgSelectProps> = function (props) {
  const [showDialog, setShowDialog] = React.useState(false);
  const selectedSvg = props.enclosingSvgs.find(svg => svg.id === props.value);

  // lazy import to avoid circular dependency
  const SvgSelectDialogRef = React.useRef<typeof import('./SvgSelectDialog').SvgSelectDialog | null>(null);
  const [dialogLoaded, setDialogLoaded] = React.useState(false);

  React.useEffect(() => {
    import('./SvgSelectDialog').then(module => {
      SvgSelectDialogRef.current = module.SvgSelectDialog;
      setDialogLoaded(true);
    });
  }, []);

  const handleSelect = (id: number | null) => {
    props.onChange(id);
    setShowDialog(false);
  };

  const SvgSelectDialog = SvgSelectDialogRef.current;

  return (
    <div className={props.containerClassName}>
      <button
        type="button"
        className={props.optionClassName}
        onClick={() => setShowDialog(true)}
      >
        {selectedSvg ? (
          <img className="EnclosingSvgSelect-svg" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(selectedSvg.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`} typeof="SVG" />
        ) : (
          <span className="EnclosingSvgSelect-none">なし</span>
        )}
      </button>
      {dialogLoaded && SvgSelectDialog && (
        <SvgSelectDialog
          open={showDialog}
          enclosingSvgs={props.enclosingSvgs}
          selectedValue={props.value}
          onSelect={handleSelect}
          onCancel={() => setShowDialog(false)}
        />
      )}
    </div>
  );
};




interface IHatchSelectProps {
  value: number;
  optionClassName?: string;
  containerClassName?: string;
  onChange: (value: number) => void;
}

export const HatchImage: React.FC<{ id: number }> = function (props) {
  switch (props.id) {
    case 0:
      return <img alt="Hathc None" src="/hatch_none.png" />;
    case 1:
      return <img alt="Hathc Line" src="/hatch_line.png" />;
    case 2:
      return <img alt="Hathc Line L" src="/hatch_lineL.png" />;
    case 3:
      return <img alt="Hathc Cross" src="/hatch_cross.png" />;
    case 4:
      return <img alt="Hathc Cross L" src="/hatch_crossL.png" />;
    case 5:
      return <img alt="Hathc Fill" src="/hatch_fill.png" />;
    case 6:
      return <img alt="Hathc DiagonalCross" src="/hatch_diagonal_cross.png" />
  }

  throw new Error('unknown line type: ' + props.id);
};

export const HatchSelect: React.FC<IHatchSelectProps> = function (props) {
  return (
    <div className={props.containerClassName}>
      {[0, 1, 2, 3, 4, 5, 6].map(i =>
        <ImageSelectOption
          className={props.optionClassName}
          selectedValue={props.value.toString()}
          value={i.toString()}
          onClick={(v) => props.onChange(parseInt(v))}
        >
          <HatchImage id={i} />
        </ImageSelectOption>
      )}
    </div>
  );
};
